import React, { useEffect, useMemo, useState } from 'react';
import { resolveSponsorForLocation } from '@/config/sponsorship';
import AdSenseUnit from '@/components/ads/AdSenseUnit';
import { User } from '@/api/entities';
import { useUserTier } from '@/hooks/useUserTier';

/**
 * FeaturedSponsor
 * Premium native sponsorship block with location-aware targeting.
 * - Tries to resolve sponsor by city/state; falls back to display ad if none.
 * - Accepts optional props to override location detection.
 */
const FeaturedSponsor = ({
  city,
  state,
  className = '',
}) => {
  // IMPORTANT: All hooks must be called before any conditional returns
  // to comply with React's Rules of Hooks
  const { tier } = useUserTier();
  const shouldShowAds = tier === 'free';
  
  const [resolvedLocation, setResolvedLocation] = useState({ city: city || null, state: state || null });
  const [loadingLocation, setLoadingLocation] = useState(false);

  // Try to fetch user home location if not provided via props
  useEffect(() => {
    let mounted = true;
    const shouldFetch = !city || !state;
    if (!shouldFetch) return;

    async function loadUserLocation() {
      try {
        setLoadingLocation(true);
        const me = await User.me().catch(() => null);
        const home = me?.locations?.home;
        if (mounted && home) {
          setResolvedLocation({
            city: home.city || null,
            state: home.state || home.state_code || null,
          });
        }
      } catch {
        // best-effort only
      } finally {
        if (mounted) setLoadingLocation(false);
      }
    }

    loadUserLocation();
    return () => { mounted = false; };
  }, [city, state]);

  const { sponsor, tier: sponsorTier } = useMemo(() => {
    const c = resolvedLocation.city;
    const s = resolvedLocation.state;
    return resolveSponsorForLocation(c, s);
  }, [resolvedLocation.city, resolvedLocation.state]);

  // Early return for premium/beta users (after all hooks are called)
  if (!shouldShowAds) {
    return null;
  }

  // Fallback to standard display ad if no sponsorship enabled
  // Always render fallback ad (required for Google approval)
  if (!sponsor) {
    return (
      <section className={`flex justify-center ${className}`}>
        <div
          className="
            bg-white rounded-xl border border-slate-200/70 shadow-lg
            px-3 py-2
            mx-auto
            w-[300px] h-[250px]
            sm:w-[468px] sm:h-[60px]
            md:w-[728px] md:h-[90px]
          "
          aria-label="Sponsored"
        >
          <AdSenseUnit
            adSlot="72890_myprograms_featured_fallback"
            adFormat="auto"
            adLayout="display"
            className="w-full h-full"
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      </section>
    );
  }

  // Premium native layout
  return (
    <section className={`relative overflow-hidden rounded-2xl border border-slate-200/70 shadow-xl ${className}`} aria-label="Sponsored">
      {/* Background image (optional) */}
      {sponsor.bgImageUrl && (
        <div className="absolute inset-0">
          <img
            src={sponsor.bgImageUrl}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/40 to-black/20" />
        </div>
      )}

      <div className="relative z-10 p-6 md:p-8 lg:p-10">
        <div className="flex items-start gap-4">
          {/* Logo */}
          {sponsor.logoUrl && (
            <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-xl bg-white/90 backdrop-blur p-2 border border-white/60 shadow-md">
              <img
                src={sponsor.logoUrl}
                alt="Sponsor logo"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Disclosure and match tier */}
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-white/90 backdrop-blur text-slate-700 text-[10px] font-semibold px-2 py-1 rounded-full border border-white/60">
                {sponsor.disclosure || 'Sponsored'}
              </span>
              {import.meta?.env?.DEV && (
                <span className="hidden sm:inline bg-white/80 text-slate-600 text-[10px] font-medium px-2 py-1 rounded-full border border-white/60">
                  {loadingLocation ? 'Resolving location…' : `Tier: ${sponsorTier}`}
                </span>
              )}
            </div>

            <h3 className="text-white text-xl md:text-2xl font-bold tracking-tight">
              {sponsor.headline}
            </h3>
            <p className="text-white/90 text-sm md:text-base mt-1 md:mt-2">
              {sponsor.blurb}
            </p>

            <div className="mt-3">
              <a
                href={sponsor.ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white shadow-lg"
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('sponsor-cta-click', {
                      detail: {
                        sponsorId: sponsor.id,
                        tier: sponsorTier,
                        city: resolvedLocation.city || null,
                        state: resolvedLocation.state || null
                      }
                    }));
                    // eslint-disable-next-line no-console
                    console.log('Sponsor CTA click', { sponsorId: sponsor.id, tier: sponsorTier });
                  } catch {
                    // no-op
                  }
                }}
              >
                {sponsor.ctaText}
                <svg xmlns="http://www.w3.org/2000/svg" className="ml-2 h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0L17 8.586a1 1 0 010 1.414l-5.293 5.293a1 1 0 11-1.414-1.414L13.586 11H4a1 1 0 110-2h9.586L10.293 4.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedSponsor;


